import React, { createContext, useContext, useState, useEffect } from 'react';
import { useBusiness } from './BusinessContext';
import { Agent, BusinessAdminContextType } from '../types/business';
import { useActivityLog } from '../hooks/useActivityLog';
import { useClientManagement } from '../hooks/useClientManagement';
import { useBusinessStats } from '../hooks/useBusinessStats';
import { useReferralManagement } from '../hooks/useReferralManagement';

// Create context with default undefined value
const BusinessAdminContext = createContext<BusinessAdminContextType | undefined>(undefined);

export function BusinessAdminProvider({ children }: { children: React.ReactNode }) {
  const { business } = useBusiness();
  
  // Agents state
  const [agents, setAgents] = useState<Agent[]>([]);
  const [isLoadingAgents, setIsLoadingAgents] = useState<boolean>(false);
  const [agentsError, setAgentsError] = useState<string | null>(null);
  const [totalAgents, setTotalAgents] = useState<number>(0);
  const [activeAgents, setActiveAgents] = useState<number>(0);
  const [isAddingAgent, setIsAddingAgent] = useState<boolean>(false);
  const [isDeletingAgent, setIsDeletingAgent] = useState<boolean>(false);
  const [isUpdatingAgent, setIsUpdatingAgent] = useState<boolean>(false);

  // Clients state using the consolidated hook (without agentEmail for admin view)
  const {
    clients,
    isLoadingClients,
    clientsError,
    totalClients,
    activeClients,
    fetchClients,
    refreshClients: refreshClientsFromHook
  } = useClientManagement({ 
    businessId: business?.id 
    // Note: No agentEmail for admin - they see all clients
  });

  // Activity log state using the custom hook
  const {
    activities,
    isLoading: isLoadingActivities,
    error: activitiesError,
    pagination: activityPagination,
    refreshActivities: refreshActivitiesHook,
    loadMoreActivities: loadMoreActivitiesHook,
    resetActivities
  } = useActivityLog({ initialLimit: 20 }); // Smaller initial limit for admin dashboard

  // Business stats state using the custom hook
  const {
    stats,
    isLoading: isLoadingStats,
    error: statsError,
    fetchStats,
    refreshStats: refreshStatsHook
  } = useBusinessStats({ businessId: business?.id });

  // Referral management using the existing hook
  const {
    referrals,
    isLoadingReferrals,
    referralsError,
    addReferral,
    updateReferral,
    deleteReferral,
    refreshReferrals,
    isAddingReferral,
    isUpdatingReferral,
    isDeletingReferral
  } = useReferralManagement({ 
    businessId: business?.id,
    userType: 'business',
    userEmail: undefined // Not needed for business admin
  });

  const fetchAgents = async () => {
    if (!business?.id) {
      console.log('No business ID available for fetching agents');
      return;
    }

    setIsLoadingAgents(true);
    setAgentsError(null);
    
    try {
      const response = await fetch(`/api/business/${business.id}/agents`, {
        method: 'GET',
        credentials: 'same-origin'
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        // Add mock delegation limits to agents data
        const agentsWithLimits = (data.agents || []).map((agent: any) => ({
          ...agent,
          delegationLimit: 15, // Mock: Business admin set this agent's limit
          currentDelegations: Math.floor(Math.random() * 15) // Mock: Random current delegations
        }));
        
        setAgents(agentsWithLimits);
        setTotalAgents(data.totalAgents || 0);
        setActiveAgents(data.activeAgents || 0);
      } else {
        setAgentsError(data.error || 'Failed to fetch agents');
        setAgents([]);
        setTotalAgents(0);
        setActiveAgents(0);
      }
    } catch (error) {
      console.error('Error fetching agents:', error);
      setAgentsError(error instanceof Error ? error.message : 'An unexpected error occurred');
      setAgents([]);
      setTotalAgents(0);
      setActiveAgents(0);
    } finally {
      setIsLoadingAgents(false);
    }
  };

  const addAgent = async (agentData: {
    userEmail: string;
    agentName: string;
    agentEmail?: string;
    agentPhone?: string;
    agentProfile?: string; 
    agentProfileUrl?: string;
    agentProfileImage?: string;
    isActive: boolean;
  }): Promise<{ success: boolean; error?: string }> => {
    if (!business?.id) {
      return { success: false, error: 'No business ID available' };
    }

    setIsAddingAgent(true);
    
    try {
      const response = await fetch(`/api/business/${business.id}/agents`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'same-origin',
        body: JSON.stringify(agentData)
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        // Add the new agent to the local state
        setAgents(prev => [...prev, data.agent]);
        setTotalAgents(prev => prev + 1);
        if (data.agent.isActive) {
          setActiveAgents(prev => prev + 1);
        }
        
        // Refresh activities and stats to show the new agent addition
        if (business?.id) {
          await refreshActivitiesHook(business.id);
          await refreshStatsHook();
        }
        
        return { success: true };
      } else {
        return { success: false, error: data.error || 'Failed to add agent' };
      }
    } catch (error) {
      console.error('Error adding agent:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'An unexpected error occurred' 
      };
    } finally {
      setIsAddingAgent(false);
    }
  };

  const updateAgent = async (
    userEmail: string,
    updates: {
      agentName?: string;
      agentEmail?: string;
      agentPhone?: string;
      agentProfile?: string;
      agentProfileUrl?: string;
      agentProfileImage?: string;
      isActive?: boolean;
    }
  ): Promise<{ success: boolean; error?: string }> => {
    if (!business?.id) {
      return { success: false, error: 'No business ID available' };
    }

    setIsUpdatingAgent(true);
    
    try {
      const response = await fetch(`/api/business/${business.id}/agents`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'same-origin',
        body: JSON.stringify({ userEmail, updates })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        // Update the agent in local state
        setAgents(prev => prev.map(agent => 
          agent.user.email === userEmail ? data.agent : agent
        ));
        
        // Update active agents count if status changed
        const originalAgent = agents.find(agent => agent.user.email === userEmail);
        if (originalAgent && updates.isActive !== undefined) {
          if (updates.isActive && !originalAgent.isActive) {
            setActiveAgents(prev => prev + 1);
          } else if (!updates.isActive && originalAgent.isActive) {
            setActiveAgents(prev => prev - 1);
          }
        }
        
        // Refresh activities and stats to show the agent update
        if (business?.id) {
          await refreshActivitiesHook(business.id);
          await refreshStatsHook();
        }
        
        return { success: true };
      } else {
        return { success: false, error: data.error || 'Failed to update agent' };
      }
    } catch (error) {
      console.error('Error updating agent:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'An unexpected error occurred' 
      };
    } finally {
      setIsUpdatingAgent(false);
    }
  };

  const deleteAgent = async (userEmail: string): Promise<{ success: boolean; error?: string }> => {
    if (!business?.id) {
      return { success: false, error: 'No business ID available' };
    }

    setIsDeletingAgent(true);
    
    try {
      const response = await fetch(`/api/business/${business.id}/agents`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'same-origin',
        body: JSON.stringify({ userEmail })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        // Remove the agent from local state
        const deletedAgent = agents.find(agent => agent.user.email === userEmail);
        setAgents(prev => prev.filter(agent => agent.user.email !== userEmail));
        setTotalAgents(prev => prev - 1);
        if (deletedAgent?.isActive) {
          setActiveAgents(prev => prev - 1);
        }
        
        // Refresh activities and stats to show the agent removal
        if (business?.id) {
          await refreshActivitiesHook(business.id);
          await refreshStatsHook();
        }
        
        return { success: true };
      } else {
        return { success: false, error: data.error || 'Failed to delete agent' };
      }
    } catch (error) {
      console.error('Error deleting agent:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'An unexpected error occurred' 
      };
    } finally {
      setIsDeletingAgent(false);
    }
  };

  // Auto-fetch data when business changes
  useEffect(() => {
    if (business?.id) {
      fetchAgents();
      fetchClients(); // Use the hook's fetch function
      refreshActivitiesHook(business.id);
      fetchStats(); // Fetch stats when business is available
    } else {
      // Reset activities when no business
      resetActivities();
    }
  }, [business?.id, refreshActivitiesHook, resetActivities, fetchClients, fetchStats]);

  // Refresh functions
  const refreshAgents = async () => {
    await fetchAgents();
  };

  const refreshClients = refreshClientsFromHook;

  const refreshActivities = async () => {
    if (business?.id) {
      await refreshActivitiesHook(business.id);
    }
  };

  const loadMoreActivities = async () => {
    if (business?.id) {
      await loadMoreActivitiesHook(business.id);
    }
  };

  const refreshStats = async () => {
    await refreshStatsHook();
  };

  return (
    <BusinessAdminContext.Provider
      value={{
        // Agents
        agents,
        isLoadingAgents,
        agentsError,
        refreshAgents,
        totalAgents,
        activeAgents,
        addAgent,
        isAddingAgent,
        updateAgent,
        isUpdatingAgent,
        deleteAgent,
        isDeletingAgent,
        
        // Clients - now using the consolidated hook with correct property names
        clients,
        isLoadingClients,
        clientsError,
        refreshClients,
        totalClients,
        activeClients,
        
        // Activity Log
        activities,
        isLoadingActivities,
        activitiesError,
        activityPagination,
        refreshActivities,
        loadMoreActivities,
        
        // Business Statistics
        stats,
        isLoadingStats,
        statsError,
        refreshStats,

        // Referrals
        referrals,
        isLoadingReferrals,
        referralsError,
        addReferral,
        updateReferral,
        deleteReferral,
        refreshReferrals,
        isAddingReferral,
        isUpdatingReferral,
        isDeletingReferral
      }}
    >
      {children}
    </BusinessAdminContext.Provider>
  );
}

export const useBusinessAdmin = () => {
  const context = useContext(BusinessAdminContext);
  if (context === undefined) {
    throw new Error('useBusinessAdmin must be used within a BusinessAdminProvider');
  }
  return context;
};